import React, { useState } from 'react';
import SEO from '../components/SEO';

const TermsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('agreement');

  const sections = [
    { id: 'agreement', label: '1. Agreement to Terms' },
    { id: 'intellectual-property', label: '2. Intellectual Property' },
    { id: 'user-responsibilities', label: '3. User Responsibilities' },
    { id: 'prohibited-activities', label: '4. Prohibited Activities' },
    { id: 'services-availability', label: '5. Services & Warranties' },
    { id: 'limitation-liability', label: '6. Limitation of Liability' },
    { id: 'governing-law', label: '7. Governing Law' },
    { id: 'changes-terms', label: '8. Changes to Terms' },
    { id: 'contact', label: '9. Contact Information' }
  ];

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset for fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Terms & Conditions - AlmeOne"
        description="Review the terms and conditions for using AlmeOne's website, AI consultation, software, and warehouse automation services."
        keywords="terms and conditions, terms of service, legal agreement, AlmeOne terms, Qatar legal terms"
      />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-gold/20 py-20 text-white">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gold">Terms &</span> Conditions
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Last updated: June 17, 2026. Please read these terms carefully before accessing or using our solutions.
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-1/4 lg:sticky lg:top-28 h-fit bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <h2 className="text-black font-bold text-lg mb-6 border-b border-gray-100 pb-3 flex items-center gap-2">
              <span className="text-gold text-xl">⚖️</span> Table of Contents
            </h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold/10 text-gold border-l-4 border-gold pl-3'
                      : 'text-gray-600 hover:text-gold hover:bg-gray-50 pl-4'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Terms text */}
          <div className="lg:w-3/4 bg-white p-8 sm:p-12 rounded-2xl shadow-lg border border-gray-100 prose prose-lg prose-gold max-w-none text-gray-700 leading-relaxed space-y-12">
            <div id="agreement" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                1. Agreement to Terms
              </h2>
              <p>
                These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and AlmeOne ("we," "us," or "our"), concerning your access to and use of our website, AI integration applications, warehouse solutions, and any other media channel or mobile website related or connected thereto.
              </p>
              <p>
                By accessing our website or services, you agree that you have read, understood, and agree to be bound by all of these Terms and Conditions. If you do not agree with all of these terms, you are expressly prohibited from using our services and must discontinue use immediately.
              </p>
            </div>

            <div id="intellectual-property" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                2. Intellectual Property Rights
              </h2>
              <p>
                Unless otherwise indicated, our website, design models, algorithm structures, proprietary automation software, source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the site (collectively, the "Content") and the trademarks, service marks, and logos contained therein (the "Marks") are owned or controlled by us or licensed to us, and are protected by copyright, trademark, and other intellectual property laws of Qatar, international jurisdictions, and conventions.
              </p>
              <p>
                No part of the website or content may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever without our express prior written permission.
              </p>
            </div>

            <div id="user-responsibilities" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                3. User Responsibilities
              </h2>
              <p>
                By using our services, you represent and warrant that:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>All registration or contact information you submit will be true, accurate, current, and complete.</li>
                <li>You will maintain the accuracy of such information and promptly update it as necessary.</li>
                <li>You have the legal capacity and authority to agree to these Terms and Conditions.</li>
                <li>You will not access our services through automated or non-human means (e.g., bot scripts), except for pre-approved custom API integrations.</li>
                <li>Your use of our services will not violate any applicable law or regulation.</li>
              </ul>
            </div>

            <div id="prohibited-activities" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                4. Prohibited Activities
              </h2>
              <p>
                You may not access or use our systems for any purpose other than that for which we make the services available. Prohibited activities include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Systematically retrieving data or other content from the site to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.</li>
                <li>Circumventing, disabling, or otherwise interfering with security-related features of our services, including features that prevent or restrict the use or copying of any content.</li>
                <li>Disparaging, tarnishing, or otherwise harming, in our opinion, us, our partners, or our services.</li>
                <li>Using any information obtained from our website or applications in order to harass, abuse, or harm another person.</li>
                <li>Using our services in a manner inconsistent with any applicable laws or regulations.</li>
              </ul>
            </div>

            <div id="services-availability" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                5. Services and Warranties
              </h2>
              <p>
                Our solutions are provided on an "as-is" and "as-available" basis. You agree that your use of our website and services will be at your sole risk. To the fullest extent permitted by law, we disclaim all warranties, express or implied, in connection with the website and your use thereof, including, without limitation, the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p>
                We make no warranties or representations about the accuracy or completeness of the site's content or the content of any websites linked to this site, and we will assume no liability or responsibility for any:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Errors, mistakes, or inaccuracies of content and materials.</li>
                <li>Personal injury or property damage, of any nature whatsoever, resulting from your access to and use of our services.</li>
                <li>Any unauthorized access to or use of our secure servers and/or any and all personal information and/or financial information stored therein.</li>
                <li>Any interruption or cessation of transmission to or from our website or software systems.</li>
              </ul>
            </div>

            <div id="limitation-liability" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                6. Limitation of Liability
              </h2>
              <p>
                In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the website or our services, even if we have been advised of the possibility of such damages.
              </p>
            </div>

            <div id="governing-law" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                7. Governing Law
              </h2>
              <p>
                These Terms and Conditions and your use of our services are governed by and construed in accordance with the laws of the State of Qatar, without regard to its conflict of law principles. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts of Doha, Qatar.
              </p>
            </div>

            <div id="changes-terms" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                8. Changes to Terms
              </h2>
              <p>
                We reserve the right, in our sole discretion, to make changes or modifications to these Terms and Conditions at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms and Conditions, and you waive any right to receive specific notice of each such change. It is your responsibility to periodically review these Terms and Conditions to stay informed of updates.
              </p>
            </div>

            <div id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                9. Contact Information
              </h2>
              <p>
                In order to resolve a complaint regarding our services or to receive further information regarding use of our website or software, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-2 mt-4 max-w-md">
                <p><strong className="text-black">Email:</strong> <a href="mailto:info@almeone.com" className="text-gold hover:underline">info@almeone.com</a></p>
                <p><strong className="text-black">Phone:</strong> +974 33920094</p>
                <p><strong className="text-black">Address:</strong> Doha, Qatar</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
